import AddIcon from '@mui/icons-material/Add';
import { Avatar, Box, Chip, Typography, useTheme, useMediaQuery } from '@mui/material';
import React from 'react';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import { ICON_BUTTON_TYPE } from '../constants';
import MedicationIcon from '@mui/icons-material/Medication';
import { isEmpty, map } from 'lodash';

const MedicationCard = ({
  name,
  frequency,
  dose,
  times,
  howToTake,
  description,
  responsible,
  duration,
  handleDeleteMedications,
  color,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        py: 3,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: color || '#26786a' }}>
            <MedicationIcon />
          </Avatar>
          <Typography variant="h6">{name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {responsible && <Chip label="Responsible" avatar={<Avatar src="/avatar.png" />} />}
          <Typography variant="body2">Duration: {duration}</Typography>
          <IconButton type={ICON_BUTTON_TYPE.delete} onClick={handleDeleteMedications} />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          mt: 2,
          flexDirection: isTablet ? 'column' : 'row',
          '& > *': {
            flex: isTablet ? '1 1 100%' : '1 1 auto',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">Frequency</Typography>
          <Typography variant="body1">{frequency}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">Dose</Typography>
          <Typography variant="body1">{dose}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">Time</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {times?.map((time, index) => (
              <Chip key={index} label={time} />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">How to take</Typography>
          <Typography variant="body1">{howToTake}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 2 }}>
        {description}
      </Typography>
    </Box>
  );
};

const MedicationsList = ({ setShowMedicationTable, medications, loader, handleDeleteMedications }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: isMobile ? 'auto' : '65vh',
        p: isMobile ? 2 : 3,
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'scroll',
      }}
      className="hide-scroll"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 2 : 0,
          mb: 3,
        }}
      >
        <Typography variant="h5">Medications</Typography>
        <Button text="Add Medications" startIcon={<AddIcon />} onClick={setShowMedicationTable} fullWidth={isMobile} />
      </Box>

      {isEmpty(medications) && (
        <Typography variant="body1" textAlign={'center'}>
          No medications found!
        </Typography>
      )}

      {!isEmpty(medications) &&
        medications.map((item, index) => (
          <MedicationCard
            key={index}
            name={item?.name || ''}
            frequency={item?.frequency === 1 ? 'Daily' : `Weekly (${item?.weekDayFrequency?.map((item) => item)})`}
            dose={`${item?.quantity || item?.timeFrequency[0]?.quantity} ${item?.type} / day`}
            times={map(item?.timeFrequency, (item) => item?.time)}
            howToTake={item?.howToTake || 'After meal'}
            description={item?.description || ''}
            responsible={item?.responsible}
            duration={`${item?.duration || 2} days`}
            handleDeleteMedications={() => handleDeleteMedications(item?.id)}
            color={item?.color}
          />
        ))}
    </Box>
  );
};

export default MedicationsList;
